// InteliChat #41 - favicon badge/flash (extends the native faviconHelper
// idea from a static /favicon-badge-*.png swap into a canvas-drawn count +
// flash). Draws a pink dot with the unread-NEW count over the live favicon
// and flashes it on the same ~1s discipline as the title cycle. Falls back
// to a title-number prefix where canvas favicons are not supported (older
// Safari). Restores the original favicon on clear.

const ACCENT = '#ec4899'; // --il-accent-2 (pink alert), matches P3-gated token
const SIZE = 32;

let originalHrefs = null; // [{el, href}] captured once so we can restore
let baseImg = null; // cached <img> of the base favicon
let flashTimer = null;
let flashOn = false;
let canvasSupported = true;

const iconLinks = () =>
  Array.from(
    document.querySelectorAll(
      'link[rel~="icon"], link[rel="shortcut icon"], link.favicon'
    )
  );

const captureOriginals = () => {
  if (originalHrefs) return;
  originalHrefs = iconLinks().map(el => ({ el, href: el.href }));
};

const loadBase = () =>
  new Promise(resolve => {
    if (baseImg) return resolve(baseImg);
    const links = iconLinks();
    const src =
      (links[0] && links[0].href) ||
      '/favicon-32x32.png';
    const img = new Image();
    img.onload = () => {
      baseImg = img;
      resolve(img);
    };
    img.onerror = () => resolve(null);
    img.src = src;
    return undefined;
  });

const drawBadge = async count => {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const g = canvas.getContext('2d');
  if (!g) {
    canvasSupported = false;
    return null;
  }
  const img = await loadBase();
  if (img) {
    try {
      g.drawImage(img, 0, 0, SIZE, SIZE);
    } catch {
      /* tainted / not ready - draw badge on blank */
    }
  }
  // badge bubble
  const r = 11;
  const cx = SIZE - r + 1;
  const cy = r - 1;
  g.beginPath();
  g.arc(cx, cy, r, 0, Math.PI * 2);
  g.fillStyle = ACCENT;
  g.fill();
  // count text
  const label = count > 9 ? '9+' : String(count);
  g.fillStyle = '#ffffff';
  g.font = 'bold 15px -apple-system, system-ui, sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(label, cx, cy + 1);
  try {
    return canvas.toDataURL('image/png');
  } catch {
    canvasSupported = false;
    return null;
  }
};

const setHref = href => {
  iconLinks().forEach(el => {
    el.href = href;
  });
};

const restore = () => {
  if (!originalHrefs) return;
  originalHrefs.forEach(({ el, href }) => {
    el.href = href;
  });
};

// title-number fallback (older Safari): prefix "(N) " on the title. The
// title-cycle module owns the full document.title; this only nudges the
// number in when canvas favicons are unavailable.
const titleFallback = count => {
  const t = document.title.replace(/^\(\d+\+?\)\s*/, '');
  document.title = count > 0 ? `(${count > 9 ? '9+' : count}) ${t}` : t;
};

// Public: show a steady count badge (no flash).
export const showFaviconCount = async count => {
  captureOriginals();
  if (!count || count < 1) {
    clearFavicon();
    return;
  }
  const url = await drawBadge(count);
  if (url && canvasSupported) {
    setHref(url);
  } else {
    titleFallback(count);
  }
};

// Public: flash the count badge on/off on ~1s interval (attention).
export const startFaviconFlash = async count => {
  captureOriginals();
  stopFaviconFlash();
  if (!count || count < 1) return;
  const badged = await drawBadge(count);
  if (!badged || !canvasSupported) {
    titleFallback(count);
    return;
  }
  flashOn = true;
  setHref(badged);
  flashTimer = setInterval(() => {
    flashOn = !flashOn;
    if (flashOn) setHref(badged);
    else restore();
  }, 1000);
};

export const stopFaviconFlash = () => {
  if (flashTimer) {
    clearInterval(flashTimer);
    flashTimer = null;
  }
  flashOn = false;
};

// Public: clear all badging + flashing, restore the original favicon.
export const clearFavicon = () => {
  stopFaviconFlash();
  restore();
  // strip any title-number fallback we added
  document.title = document.title.replace(/^\(\d+\+?\)\s*/, '');
};
